// ─────────────────────────────────────────────────────────────────────────────
// Sección "Recap" del Admin (módulo `wrapped` del demo, fundamentales-admin.js : 1224).
//
// Paridad demo, bloque a bloque: edición + nombre visible + nueva edición · alcance OU, interruptor
// y ventana de visibilidad + estado de la notificación · tabla de slides con vista previa, título y
// activo (editor por slide en modal) · caja de difusión · mensaje genérico de respaldo (imagen +
// textos ×7) · historial de ediciones con sus ganadores anuales.
//
// Slides por OU (war room 2026-10-20): «Configurar slides para» elige Global o una OU. En Global se
// edita la configuración base; con una OU, encender/apagar un slide cambia SOLO a esa OU (y a sus
// sub-OUs que no tengan la suya). Antes, elegir «México» y apagar slides los apagaba para todas.
//
// Varias configuraciones por año (2026-10-22, «Configuración de Wrapped por OU»): cada una con su
// alcance, estado, ventana y notificación. «Nueva configuración» crea otra del mismo año sin tocar las
// existentes (antes, dar el Wrapped a otra OU obligaba a cambiar el alcance de la única edición, y la
// OU configurada primero se quedaba sin Wrapped). El selector y el historial van por configuración.
//
// Cambios Resumen Anual (2026-11-03, doc de Alejandro Pérez y war room 2026-11-02): «Nueva edición» pide
// el año (ya no salta solo al siguiente), Guardar renombra la configuración con su alcance cuando éste
// cambia («Recap 2033 · MEXICO, USA, FOSE +2»), el historial permite eliminar las inactivas sin
// notificar, y un aviso dice si la vista «Resumen» está apagada en Vistas o en Gestión.
//
// Datos REALES (v1/admin/recap). Un único botón Guardar: el PUT reemplaza la edición de forma
// atómica, igual que Gestión — nada persiste sin Guardar. El conteo de ganadores del historial es el
// de la colección `winners` del año; si no hay, es 0 (nunca relleno). Estados honestos
// (loading/error/vacío) y CERO mocks.
//
// Permisos: la matriz del cliente reserva "Gestionar Recap" a Admin General y Admin Fundamentales;
// el backend responde 403 al resto y aquí se muestra el mismo bloqueo antes de pedir datos.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  useRecapEditions, useRecapEdition, useSaveRecapEdition, useCreateRecapEdition,
  useNotifyRecapEdition, useDeleteRecapEdition, useGestionSettings, useUserViewsForOus,
  getRecognitionErrorMessage, recapConfigId,
} from '@/api/recognition';
import type { RecapEditionDto, RecapEditionSummaryDto, RecapSlideDto, LocalizedText } from '@/api/recognition';
import type { OUItem } from '@/api/types';
import { OuTreeSelect, collectDescendantItems } from '@/components/common/OuTree';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/common/Modal';
import { useOUItems } from '@/hooks/useOUs';
import { useUnsavedChangesGuard } from '@/contexts/UnsavedChangesContext';
import { RoleGuard } from '@/auth/RoleGuard';
import { CatalogTranslateButton } from '@/views/AdminView/tabs/TemplatesTab/CatalogTranslateButton';
import { StateBlock } from '../components';
import { AdminSection, AdminSaveBar, AdminPagination, usePagination, LocalizedFields, hasAllLanguages } from './adminShared';
import { RecapSlideEditor } from './RecapSlideEditor';
import { applyFilled, toLangTexts, toNotifLang } from './recapTranslation';
import { estiloDeFondo } from '../recapFondo';
import { dateTimeLocalToIso, isoToDateTimeLocal } from './datetimeLocal';
import {
  cambiarSlide, clavesEncendidasEnAlgunaOu, configuracionVigente, conNombresDeOu, quitarPersonalizacion, slidesParaVista,
} from '../recapSlidesPorOu';

/**
 * Nombre de cada OU del alcance; un valor que no es id del catálogo (ediciones guardadas con la lista
 * fija anterior, p. ej. «México») se muestra tal cual.
 */
const nombresDeOus = (ids: string[], ous: OUItem[]): string =>
  ids.map((id) => ous.find((o) => o.id === id)?.name ?? id).join(', ');

/**
 * 2026-10-30 — Sub-OUs (en cualquier nivel) de las OUs del alcance, para que el aviso llegue a toda la
 * gente de la OU elegida y no solo a quien está directamente en ella. Global (vacío) no lleva ninguna.
 */
export const subOusDelAlcance = (alcance: string[], ous: OUItem[]): string[] =>
  [...new Set(alcance.flatMap((id) => collectDescendantItems(ous, id).map((ou) => ou.id)))];

/** Id de la primera configuración del historial (año más reciente); la que se abre por omisión. */
const idDeLaPrimera = (filas: RecapEditionSummaryDto[] | undefined): string | undefined =>
  filas?.length ? recapConfigId(filas[0]) : undefined;

/** Alcance de una configuración para el selector y el historial; vacío = Global. */
const textoDeAlcance = (alcance: string[], ous: OUItem[], global: string): string =>
  alcance.length ? nombresDeOus(alcance, ous) : global;

/** Cuántas OUs se nombran en el nombre visible antes de resumir el resto como «+N». */
const OUS_EN_NOMBRE = 3;

/**
 * 2026-11-03 — nomenclatura del «Nombre visible para el usuario» a partir del alcance:
 * Global (vacío) → «Recap 2033»; con OUs → «Recap 2033 · MEXICO, USA, FOSE +2» (hasta 3 nombres, en el
 * orden del alcance, y el resto como «+N»). «Recap» es nombre de producto: no se traduce.
 */
export const nombreVisibleDeAlcance = (anio: number, alcance: string[], ous: OUItem[]): string => {
  if (alcance.length === 0) return `Recap ${anio}`;
  const nombres = alcance.map((id) => ous.find((o) => o.id === id)?.name ?? id);
  const resto = nombres.length - OUS_EN_NOMBRE;
  return `Recap ${anio} · ${nombres.slice(0, OUS_EN_NOMBRE).join(', ')}${resto > 0 ? ` +${resto}` : ''}`;
};

/** Mismo conjunto de OUs, sin importar orden ni mayúsculas. */
const mismoAlcance = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((x) => b.some((y) => y.toLowerCase() === x.toLowerCase()));

/**
 * Regla del nombre visible al Guardar: se reescribe con `nombreVisibleDeAlcance` SOLO si el alcance
 * cambió respecto a lo guardado y el admin no editó el nombre en ese mismo borrador. Si solo cambió el
 * nombre (o cambiaron los dos), se respeta lo que el admin escribió.
 */
export const nombreAlGuardar = (borrador: RecapEditionDto, guardada: RecapEditionDto | undefined, ous: OUItem[]): string => {
  if (!guardada) return borrador.nombre;
  const alcanceCambio = !mismoAlcance(borrador.alcance, guardada.alcance);
  const nombreSinTocar = borrador.nombre === guardada.nombre;
  return alcanceCambio && nombreSinTocar ? nombreVisibleDeAlcance(borrador.anio, borrador.alcance, ous) : borrador.nombre;
};

/** Años que ofrece «Nueva edición»: del año anterior al actual hasta cinco años adelante. */
export const rangoDeAniosRecap = (hoy: number = new Date().getFullYear()) => ({ min: hoy - 1, max: hoy + 5 });

/** El slide de ganador toma su icono de la insignia real configurada en Ganadores. */
const AUTO_ICON_KEY = 'ganador';

// `datetime-local` ⇄ ISO: hora LOCAL del admin en los dos sentidos (`datetimeLocal.ts`).
// QA 2026-10-28 — antes se mostraba con `iso.slice(0, 16)` (hora UTC) pero se guardaba como hora
// local: cada cambio en el selector movía la hora 6 h en México (elegir 04:59 p. m. pintaba
// 10:59 p. m.) y podía brincar al día siguiente.

const hasAnyText = (value: LocalizedText): boolean => Object.values(value).some((v) => (v ?? '').trim().length > 0);

export function AdminRecap() {
  return (
    <RoleGuard
      allow={['AdminGlobal', 'AdminBrillamos']}
      fallback={<RecapDenied />}
    >
      <RecapContent />
    </RoleGuard>
  );
}

function RecapDenied() {
  const { t } = useTranslation('recognition');
  return (
    <AdminSection title={t('admin.recap.title')} description={t('admin.recap.desc')}>
      <div className="adm-denied" role="alert"><i className="fas fa-lock" /> {t('admin.recap.deniedRole')}</div>
    </AdminSection>
  );
}

function RecapContent() {
  const { t, i18n } = useTranslation('recognition');
  const editions = useRecapEditions();
  const [configId, setConfigId] = useState<string | undefined>();
  const [draft, setDraft] = useState<RecapEditionDto | null>(null);
  const [dirty, setDirty] = useState(false);
  useUnsavedChangesGuard(dirty);
  const [editing, setEditing] = useState<RecapSlideDto | null>(null);
  const [preview, setPreview] = useState(false);
  /**
   * Configuración cuyo último Guardar terminó bien. «Cambios guardados» se pinta solo para ELLA: antes
   * se leía `save.isSuccess`, que no se reinicia al cambiar de configuración, y el mensaje se arrastraba
   * a la siguiente que se abría.
   */
  const [savedId, setSavedId] = useState<string | null>(null);
  /** Año capturado en el diálogo «Nueva edición»; `null` = diálogo cerrado. */
  const [newYear, setNewYear] = useState<string | null>(null);
  const [aEliminar, setAEliminar] = useState<RecapEditionSummaryDto | null>(null);
  /** Ids eliminados en esta sesión: salen del selector y del historial sin esperar a que se relea la lista. */
  const [eliminadas, setEliminadas] = useState<string[]>([]);
  /** OU cuyos slides se están configurando. `null` = Global (la base que heredan las demás). */
  const [ouConfig, setOuConfig] = useState<string | null>(null);
  // 2026-10-20 — el alcance se elige del MISMO catálogo de OUs que el paso Audiencia de Campañas
  // (UserAdminService), no de una lista fija: guarda ids de OU, que es lo que compara el servidor
  // (con la cadena de ancestras, así que elegir «MEXICO» alcanza a sus sub-OUs).
  const { data: ouItems = [] } = useOUItems({ activeOnly: false, rootsOnly: false });

  const filas = editions.data?.filter((e) => !eliminadas.includes(recapConfigId(e)));
  // Configuración seleccionada: la primera del historial (año más reciente) hasta que el admin elija otra.
  const selectedId = configId ?? idDeLaPrimera(filas);
  const edition = useRecapEdition(selectedId);
  const save = useSaveRecapEdition();
  const create = useCreateRecapEdition();
  const notify = useNotifyRecapEdition();
  const del = useDeleteRecapEdition();

  // Aviso (no bloquea Guardar): la vista «Resumen» apagada en Vistas para alguna OU del alcance (o en el
  // documento global si el alcance es Global), o el interruptor maestro de Gestión apagado.
  const alcanceDeVistas: Array<string | undefined> = draft?.alcance.length ? draft.alcance : [undefined];
  const vistasDelAlcance = useUserViewsForOus(alcanceDeVistas);
  const gestion = useGestionSettings();

  // Sincroniza el borrador cuando llega/cambia la configuración.
  if (edition.data && (draft === null || recapConfigId(draft) !== recapConfigId(edition.data))) {
    setDraft(edition.data);
    setDirty(false);
  }

  const elegirConfiguracion = (id: string) => { setConfigId(id); setDraft(null); setOuConfig(null); setSavedId(null); };

  const patch = (p: Partial<RecapEditionDto>) => { setDraft((d) => (d ? { ...d, ...p } : d)); setDirty(true); };

  const patchSlide = (next: RecapSlideDto) => {
    setDraft((d) => (d ? { ...d, slides: d.slides.map((s) => (s.clave === next.clave ? next : s)) } : d));
    setDirty(true);
  };

  const pager = usePagination(draft?.slides ?? [], 10);

  const activeSlides = draft?.slides.filter((s) => s.activo) ?? [];
  // Un slide encendido en Global O en alguna OU necesita su título en los 7 idiomas (igual que el servidor).
  const encendidosEnOus = draft ? clavesEncendidasEnAlgunaOu(draft) : new Set<string>();
  const slidesOk = activeSlides.length > 0
    && (draft?.slides ?? []).every((s) => (!s.activo && !encendidosEnOus.has(s.clave)) || hasAllLanguages(s.titulo));
  const vigente = draft ? configuracionVigente(draft, ouConfig, ouItems) : null;
  const nombreOu = (id: string) => ouItems.find((o) => o.id === id)?.name ?? id;
  /** La OU elegida tiene su propia personalización (no la hereda). */
  const isOuPersonalizada = ouConfig !== null && vigente?.origen?.toLowerCase() === ouConfig.toLowerCase();
  const notaOrigen = (() => {
    if (ouConfig === null) return t('admin.recap.slidesForGlobalNote');
    if (isOuPersonalizada) return t('admin.recap.slidesForCustomNote', { ou: nombreOu(ouConfig) });
    return t('admin.recap.slidesForInheritedNote', {
      ou: nombreOu(ouConfig),
      from: vigente?.origen ? nombreOu(vigente.origen) : t('admin.recap.slidesForGlobal'),
    });
  })();
  const toggleSlide = (claveSlide: string, isEncendido: boolean) => {
    setDraft((d) => (d ? cambiarSlide(d, { ouId: ouConfig, claveSlide, isEncendido }, ouItems) : d));
    setDirty(true);
  };
  const msg = draft?.mensajeGenerico;
  const genericTouched = Boolean(msg && (hasAnyText(msg.titulo) || hasAnyText(msg.descripcion)));
  const genericOk = !genericTouched || Boolean(msg && hasAllLanguages(msg.titulo) && hasAllLanguages(msg.descripcion));
  const canSave = Boolean(draft?.nombre.trim()) && slidesOk && genericOk;

  const doSave = () => {
    if (!draft) return;
    const aGuardar: RecapEditionDto = { ...draft, nombre: nombreAlGuardar(draft, edition.data, ouItems) };
    save.mutate(aGuardar, {
      onSuccess: (guardada) => {
        setDraft(guardada ?? aGuardar);
        setDirty(false);
        setSavedId(recapConfigId(aGuardar));
      },
    });
  };

  const abrirCreada = (created: RecapEditionDto) => {
    setConfigId(recapConfigId(created)); setDraft(created); setDirty(false); setOuConfig(null); setSavedId(null);
  };

  // 2026-11-03 — «Nueva edición» pide el año. Antes tomaba el año siguiente al mayor del historial: una
  // segunda edición de 2026 acababa como 2027, y nadie la veía (se reportó como «activar una desactiva
  // la otra»).
  const rangoAnios = rangoDeAniosRecap();
  const anioNuevo = newYear === null ? Number.NaN : Number(newYear);
  const anioNuevoOk = Number.isInteger(anioNuevo) && anioNuevo >= rangoAnios.min && anioNuevo <= rangoAnios.max;
  const abrirNuevaEdicion = () => { create.reset?.(); setNewYear(String(new Date().getFullYear())); };
  const doCreate = () => {
    if (!anioNuevoOk) return;
    create.mutate(
      { anio: anioNuevo, nombre: nombreVisibleDeAlcance(anioNuevo, [], ouItems) },
      { onSuccess: (created) => { setNewYear(null); abrirCreada(created); } });
  };

  /** Motivo por el que una fila del historial no se puede eliminar; `null` = se puede. */
  const motivoNoEliminable = (e: RecapEditionSummaryDto): string | null => {
    if (e.estado !== 'borrador') return t('admin.recap.deleteBlockedActive');
    const notificada = e.notificadoAt
      ?? (recapConfigId(e) === selectedId ? edition.data?.notificadoAt : null);
    return notificada ? t('admin.recap.deleteBlockedNotified') : null;
  };

  const doDelete = (fila: RecapEditionSummaryDto) => {
    const id = recapConfigId(fila);
    del.mutate(id, {
      onSuccess: () => {
        setEliminadas((prev) => [...prev, id]);
        if (id !== selectedId) return;
        const otra = filas?.find((e) => recapConfigId(e) !== id);
        setConfigId(otra ? recapConfigId(otra) : undefined);
        setDraft(null); setOuConfig(null); setSavedId(null); setDirty(false);
      },
    });
  };

  /** Otra configuración del MISMO año (otra OU o grupo de OUs); las existentes no cambian. */
  const doCreateConfig = () => {
    if (!draft) return;
    create.mutate(
      { anio: draft.anio, nombre: t('admin.recap.newConfigName', { year: draft.anio }) },
      { onSuccess: abrirCreada });
  };

  const alcanceTexto = (alcance: string[]) => textoDeAlcance(alcance, ouItems, t('admin.recap.scopeGlobal'));

  const resumenApagadoEn = alcanceDeVistas
    .filter((_, i) => vistasDelAlcance[i]?.data?.resumenMensual === false)
    .map((id) => (id ? nombreOu(id) : t('admin.recap.scopeGlobal')));
  const gestionApagada = gestion.data?.wrappedByOu === false;

  const slideName = (clave: string) => t(`admin.recap.slides.${clave}.name`);
  const slideHint = (clave: string) => t(`admin.recap.slides.${clave}.hint`);
  const titleFor = (s: RecapSlideDto) => s.titulo[i18n.language] ?? s.titulo.es ?? '';

  return (
    <AdminSection title={t('admin.recap.title')} description={t('admin.recap.desc')}>
      <StateBlock
        isLoading={editions.isLoading || edition.isLoading}
        isError={editions.isError || edition.isError}
        error={editions.error ?? edition.error}
        isEmpty={!editions.isLoading && (filas?.length ?? 0) === 0}
        emptyText={t('admin.recap.noEditions')}
        onRetry={() => { editions.refetch(); edition.refetch(); }}
      >
        {/* `selectedId` manda: si la lista de ediciones se queda vacía, no se sigue mostrando
            el borrador de una configuración que ya no está. */}
        {draft && selectedId === recapConfigId(draft) && (
          <>
            <AdminSaveBar
              onSave={doSave} saving={save.isPending} dirty={dirty && canSave} saved={savedId !== null && savedId === selectedId}
              disabledReason={dirty && !canSave ? t('admin.recap.fixValidation') : null}
            />
            {save.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {conNombresDeOu(getRecognitionErrorMessage(save.error, t('admin.saveError')), ouItems)}
              </p>
            )}

            {/* ── Edición ─────────────────────────────────────────────── */}
            <div className="adm-recap-row">
              <label className="adm-field">
                <span>{t('admin.recap.edition')}</span>
                <select className="field"
                  value={selectedId ?? ''}
                  onChange={(e) => elegirConfiguracion(e.target.value)}
                >
                  {filas?.map((e) => (
                    <option key={recapConfigId(e)} value={recapConfigId(e)}>
                      {e.anio} · {e.nombre} · {alcanceTexto(e.alcance)}
                    </option>
                  ))}
                </select>
              </label>

              <label className="adm-field adm-recap-name">
                <span>{t('admin.recap.editionName')}</span>
                <input type="text" value={draft.nombre} onChange={(e) => patch({ nombre: e.target.value })} />
              </label>

              <button type="button" className="btn btn--secondary" onClick={doCreateConfig} disabled={create.isPending}>
                <i className="fas fa-sitemap" /> {t('admin.recap.newConfig')}
              </button>
              <button type="button" className="btn btn--primary" onClick={abrirNuevaEdicion} disabled={create.isPending}>
                <i className="fas fa-plus" /> {t('admin.recap.newEdition')}
              </button>
            </div>
            <p className="adm-note adm-note--info"><i className="fas fa-layer-group" /> {t('admin.recap.configsHint')}</p>
            {create.isError && newYear === null && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(create.error, t('admin.saveError'))}
              </p>
            )}

            {/* ── Difusión ─────────────────────────────────────────────────────────────────────────
                2026-10-30 — subió de debajo de la tabla de slides a debajo del selector de edición. Manda
                el alcance con sus sub-OUs: NotificationService busca por OU exacta y, con solo «CENTRAL»,
                no le llegaba a nadie de sus plantas. */}
            <div className="adm-recap-notify">
              <div>
                <h4><i className="fas fa-bell" /> {t('admin.recap.notifyTitle')}</h4>
                <p>{t('admin.recap.notifyDesc', { year: draft.anio })}</p>
              </div>
              <div className="adm-recap-notify-actions">
                <button type="button" className="btn btn--secondary" onClick={() => setPreview(true)}>
                  <i className="fas fa-eye" /> {t('admin.recap.preview')}
                </button>
                <button type="button" className="btn btn--primary" disabled={!draft.activo || notify.isPending}
                  onClick={() => notify.mutate({ id: recapConfigId(draft), subOus: subOusDelAlcance(draft.alcance, ouItems) })}>
                  <i className="fas fa-paper-plane" /> {t('admin.recap.send')}
                </button>
              </div>
            </div>
            {!draft.activo && <p className="adm-note adm-note--warn"><i className="fas fa-circle-info" /> {t('admin.recap.notifyNeedsActive')}</p>}
            {notify.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(notify.error, t('admin.recap.notifyError'))}
              </p>
            )}

            {/* ── WP-F7 / RN-PR10 · lock de edición durante la difusión ──────────────────────────
                🔴 El aviso «tu Recap ya está listo» hace una promesa concreta: a QUIÉN y ENTRE QUÉ
                FECHAS. Desde ese momento el servidor rechaza ENCOGERLA —quitar OUs del alcance,
                retrasar el inicio, adelantar el fin—, y permite ampliarla.

                Los campos NO se deshabilitan, a propósito: ampliar es legítimo y bloquearlos dejaría
                atrapado a quien anuncia con fin el 31-dic y luego necesita alargar. Y la regla NO se
                replica aquí: una segunda copia derivaría de la del contrato en cuanto una se tocara
                —es lo mismo que se decidió en `F1` con RN-R03—. El servidor es la autoridad y su
                mensaje ya se pinta; esta nota existe para que nadie descubra la regla por un error. */}
            {draft.notificadoAt && (
              <p className="adm-note adm-note--info" role="note">
                <i className="fas fa-lock" /> {t('admin.recap.difusionLockHint')}
              </p>
            )}

            {/* ── Alcance, interruptor y ventana de visibilidad ────────── */}
            <div className="adm-recap-row">
              {/* div y no label: un <label> reenvía el clic al primer botón del árbol. Varias OUs a la vez,
                  con casillas como en Audiencia de Campañas; el árbol guarda el formato colapsado (marcar
                  MEXICO guarda solo MEXICO, que implica sus sub-OUs, y así lo compara el servidor). Sin
                  ninguna marcada = «Global (todas)». */}
              <div className="adm-field">
                <span>{t('admin.recap.scope')}</span>
                <OuTreeSelect
                  items={ouItems}
                  mode="multi"
                  variant="dropdown"
                  value={draft.alcance}
                  onChange={(alcance) => patch({ alcance })}
                  placeholder={t('admin.recap.scopeGlobal')}
                />
              </div>

              <div className="adm-field">
                <span>{t('admin.recap.active')}</span>
                <label className="adm-inline-toggle">
                  <input type="checkbox" checked={draft.activo} onChange={(e) => patch({ activo: e.target.checked })} />
                  {draft.activo ? t('admin.recap.activeOn') : t('admin.recap.activeOff')}
                </label>
              </div>

              <label className="adm-field">
                <span>{t('admin.recap.visibleFrom')}</span>
                <input type="datetime-local" value={isoToDateTimeLocal(draft.visibleDesde)}
                  onChange={(e) => patch({ visibleDesde: dateTimeLocalToIso(e.target.value) })} />
              </label>

              <label className="adm-field">
                <span>{t('admin.recap.visibleTo')}</span>
                <input type="datetime-local" value={isoToDateTimeLocal(draft.visibleHasta)}
                  onChange={(e) => patch({ visibleHasta: dateTimeLocalToIso(e.target.value) })} />
              </label>

              <div className="adm-field">
                <span>{t('admin.recap.notification')}</span>
                {draft.notificadoAt ? (
                  <span className="adm-status adm-status--on">
                    <i className="fas fa-check-circle" />{' '}
                    {t('admin.recap.notifiedAt', { date: new Date(draft.notificadoAt).toLocaleDateString(i18n.language) })}
                  </span>
                ) : (
                  <span className="adm-status adm-status--off">{t('admin.recap.neverNotified')}</span>
                )}
              </div>
            </div>

            {(resumenApagadoEn.length > 0 || gestionApagada) && (
              <div role="status" data-testid="recap-vistas-aviso">
                {gestionApagada && (
                  <p className="adm-note adm-note--warn"><i className="fas fa-eye-slash" /> {t('admin.recap.gestionOff')}</p>
                )}
                {resumenApagadoEn.length > 0 && (
                  <p className="adm-note adm-note--warn">
                    <i className="fas fa-eye-slash" /> {t('admin.recap.vistasOff', { ous: resumenApagadoEn.join(', ') })}
                  </p>
                )}
              </div>
            )}

            {/* ── Slides ───────────────────────────────────────────────── */}
            <h3 className="adm-subtitle">{t('admin.recap.slidesTitle', { count: draft.slides.length })}</h3>
            <p className="adm-note">{t('admin.recap.slidesHint')}</p>
            <p className="adm-note adm-note--info"><i className="fas fa-globe" /> {t('admin.recap.i18nHint')}</p>

            {/* ── Slides por OU ─────────────────────────────────────────── */}
            <div className="adm-recap-row">
              <div className="adm-field">
                <span>{t('admin.recap.slidesFor')}</span>
                <OuTreeSelect
                  items={ouItems}
                  mode="single"
                  variant="dropdown"
                  value={ouConfig ? [ouConfig] : []}
                  onChange={(ids) => setOuConfig(ids[0] ?? null)}
                  placeholder={t('admin.recap.slidesForGlobal')}
                />
              </div>
            </div>
            <p className="adm-note adm-note--info" role="status" data-testid="recap-slides-origen">
              <i className="fas fa-sitemap" /> {notaOrigen}
              {isOuPersonalizada && ouConfig !== null && (
                <button type="button" className="btn btn--link"
                  onClick={() => { setDraft((d) => (d ? quitarPersonalizacion(d, ouConfig) : d)); setDirty(true); }}>
                  <i className="fas fa-rotate-left" /> {t('admin.recap.slidesForRemoveCustom')}
                </button>
              )}
            </p>
            {(draft.slidesPorOu ?? []).length > 0 && (
              <p className="adm-note">
                {t('admin.recap.customizedOus')}{' '}
                {(draft.slidesPorOu ?? []).map((p) => (
                  <button key={p.ouId} type="button" className="btn btn--link" onClick={() => setOuConfig(p.ouId)}>
                    {nombreOu(p.ouId)} ({t('admin.recap.customizedOuCount', { count: p.slidesActivos.length })})
                  </button>
                ))}
              </p>
            )}

            <div className="adm-table-wrap">
              <table className="adm-table adm-table--glass">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('admin.recap.colSlide')}</th>
                    <th>{t('admin.recap.colPreview')}</th>
                    <th>{t('admin.recap.colTitle')}</th>
                    <th>{t('admin.recap.colActive')}</th>
                    <th>{t('admin.recap.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pager.slice.map((s) => {
                    const encendido = vigente?.claves.has(s.clave) ?? s.activo;
                    // Nunca se deja una OU (ni Global) sin ningún slide: el servidor lo rechazaría al guardar.
                    const esElUltimo = encendido && (vigente?.claves.size ?? 0) <= 1;
                    return (
                    <tr key={s.clave}>
                      <td className="adm-recap-index">{s.indice}</td>
                      <td>
                        <strong>{slideName(s.clave)}</strong>
                        <span className="adm-recap-slide-hint">{slideHint(s.clave)}</span>
                      </td>
                      <td>
                        <span className="adm-recap-swatch-cell" style={estiloDeFondo(s)}>
                          {s.clave === AUTO_ICON_KEY ? <i className="fas fa-award" /> : s.icono}
                        </span>
                      </td>
                      <td>
                        <span className={titleFor(s) ? '' : 'adm-recap-missing'}>
                          {titleFor(s) || t('admin.recap.titleMissing')}
                        </span>
                        {(s.activo || encendidosEnOus.has(s.clave)) && !hasAllLanguages(s.titulo) && (
                          <span className="adm-recap-missing"><i className="fas fa-language" /> {t('admin.recap.needAllLangs')}</span>
                        )}
                      </td>
                      <td>
                        <label className="adm-inline-toggle">
                          <input type="checkbox" checked={encendido} disabled={esElUltimo}
                            title={esElUltimo ? t('admin.recap.lastSlideOn') : undefined}
                            aria-label={`${t('admin.recap.colActive')} · ${slideName(s.clave)}`}
                            onChange={(e) => toggleSlide(s.clave, e.target.checked)} />
                        </label>
                      </td>
                      <td>
                        <button type="button" className="btn btn--link" onClick={() => setEditing(s)}>
                          <i className="fas fa-pen" /> {t('admin.recap.edit')}
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <AdminPagination page={pager.page} totalPages={pager.totalPages} pageSize={pager.pageSize}
              onPage={pager.setPage} onPageSize={pager.setPageSize} />

            {/* ── Mensaje genérico de respaldo ─────────────────────────── */}
            <h3 className="adm-subtitle">{t('admin.recap.genericTitle')}</h3>
            <p className="adm-note">{t('admin.recap.genericDesc')}</p>
            <div className="adm-recap-generic">
              <div className="adm-recap-generic-image">
                <input type="url" value={draft.mensajeGenerico.imagenUrl ?? ''}
                  placeholder={t('admin.recap.imageUrlPlaceholder')}
                  onChange={(e) => patch({
                    mensajeGenerico: { ...draft.mensajeGenerico, imagenUrl: e.target.value || null },
                  })} />
                {draft.mensajeGenerico.imagenUrl
                  ? <img src={draft.mensajeGenerico.imagenUrl} alt="" className="adm-recap-generic-preview" />
                  : <div className="adm-recap-generic-preview adm-recap-generic-preview--empty"><i className="fas fa-image" /></div>}
              </div>
              <div className="adm-recap-generic-fields">
                {/* Un solo botón traduce título y descripción (mismo criterio que el Catálogo: un botón por
                    bloque). Aplica sobre el estado vigente, no sobre `draft` de este render, para no pisar
                    lo que se escriba mientras la traducción está en vuelo. */}
                <CatalogTranslateButton
                  fields={{
                    titulo: toLangTexts(draft.mensajeGenerico.titulo),
                    descripcion: toLangTexts(draft.mensajeGenerico.descripcion),
                  }}
                  activeLang={toNotifLang(i18n.language)}
                  onFilled={(filled) => {
                    if (Object.keys(filled).length === 0) return;
                    setDraft((d) => (d ? {
                      ...d,
                      mensajeGenerico: {
                        ...d.mensajeGenerico,
                        titulo: applyFilled(d.mensajeGenerico.titulo, filled.titulo),
                        descripcion: applyFilled(d.mensajeGenerico.descripcion, filled.descripcion),
                      },
                    } : d));
                    setDirty(true);
                  }}
                />
                <LocalizedFields
                  label={t('admin.recap.genericTitleLabel')}
                  value={draft.mensajeGenerico.titulo}
                  requireAll={genericTouched}
                  onChange={(titulo) => patch({ mensajeGenerico: { ...draft.mensajeGenerico, titulo } })}
                />
                <LocalizedFields
                  label={t('admin.recap.genericDescLabel')}
                  value={draft.mensajeGenerico.descripcion}
                  textarea
                  requireAll={genericTouched}
                  onChange={(descripcion) => patch({ mensajeGenerico: { ...draft.mensajeGenerico, descripcion } })}
                />
                <p className="adm-note adm-note--info"><i className="fas fa-circle-info" /> {t('admin.recap.genericNote')}</p>
                {genericTouched && !genericOk && (
                  <p className="adm-note adm-note--warn"><i className="fas fa-language" /> {t('admin.recap.needAllLangs')}</p>
                )}
              </div>
            </div>

            {/* ── Historial ────────────────────────────────────────────── */}
            <h3 className="adm-subtitle">{t('admin.recap.historyTitle')}</h3>
            <p className="adm-note">{t('admin.recap.historyDesc')}</p>
            <div className="adm-table-wrap">
              <table className="adm-table adm-table--glass">
                <thead>
                  <tr>
                    <th>{t('admin.recap.colEdition')}</th>
                    <th>{t('admin.recap.colYear')}</th>
                    <th>{t('admin.recap.colScope')}</th>
                    <th>{t('admin.recap.colSlides')}</th>
                    <th>{t('admin.recap.colWinners')}</th>
                    <th>{t('admin.recap.colStatus')}</th>
                    <th>{t('admin.recap.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filas?.map((e) => {
                    const motivo = motivoNoEliminable(e);
                    return (
                    <tr key={recapConfigId(e)} className={recapConfigId(e) === recapConfigId(draft) ? 'adm-row--selected' : ''}>
                      <td>
                        <button type="button" className="btn btn--link"
                          aria-label={t('admin.recap.openConfig', { name: e.nombre })}
                          onClick={() => elegirConfiguracion(recapConfigId(e))}>
                          <strong>{e.nombre}</strong>
                        </button>
                      </td>
                      <td>{e.anio}</td>
                      <td>{alcanceTexto(e.alcance)}</td>
                      <td>{e.slides}</td>
                      <td>
                        <span className="adm-recap-winners">
                          <span className="adm-recap-winners-badge">🏆</span>
                          {t('admin.recap.winnersCount', { count: e.ganadoresAnuales })}
                        </span>
                      </td>
                      <td>
                        <span className={`adm-status ${e.estado === 'activa' || e.estado === 'programada' ? 'adm-status--on' : 'adm-status--off'}`}>
                          {t(`admin.recap.status.${e.estado}`)}
                        </span>
                      </td>
                      <td>
                        {/* El span lleva el motivo: un botón deshabilitado no muestra su `title` en todos los navegadores. */}
                        <span title={motivo ?? undefined}>
                          <button type="button" className="btn btn--link"
                            aria-label={t('admin.recap.deleteAria', { name: e.nombre })}
                            disabled={motivo !== null || del.isPending}
                            onClick={() => { del.reset?.(); setAEliminar(e); }}>
                            <i className="fas fa-trash" /> {t('admin.recap.delete')}
                          </button>
                        </span>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {del.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(del.error, t('admin.recap.deleteError'))}
              </p>
            )}

            {editing && (
              <RecapSlideEditor
                anio={draft.anio}
                configId={draft.id ?? null}
                slide={editing}
                autoIcon={editing.clave === AUTO_ICON_KEY}
                onCancel={() => setEditing(null)}
                onSave={(next) => { patchSlide(next); setEditing(null); }}
              />
            )}

            {preview && (
              <RecapPreview
                edition={draft}
                slides={slidesParaVista(draft, ouConfig, ouItems)}
                onClose={() => setPreview(false)}
              />
            )}
          </>
        )}
      </StateBlock>

      {/* Sin ediciones todavía: el estado vacío ofrece crear la primera (nada precargado). */}
      {!editions.isLoading && !editions.isError && (filas?.length ?? 0) === 0 && (
        <button type="button" className="btn btn--primary" onClick={abrirNuevaEdicion} disabled={create.isPending}>
          <i className="fas fa-plus" /> {t('admin.recap.createFirst')}
        </button>
      )}

      {newYear !== null && (
        <Modal open onClose={() => setNewYear(null)} maxWidth={440} labelledBy="recap-new-edition-title">
          <ModalHeader onClose={() => setNewYear(null)}>
            <span id="recap-new-edition-title">{t('admin.recap.newEditionTitle')}</span>
          </ModalHeader>
          <ModalBody>
            <label className="adm-field">
              <span>{t('admin.recap.newEditionYear')}</span>
              <input type="number" inputMode="numeric" step={1}
                min={rangoAnios.min} max={rangoAnios.max} value={newYear}
                onChange={(e) => setNewYear(e.target.value)} />
            </label>
            <p className="adm-note">{t('admin.recap.newEditionYearRange', rangoAnios)}</p>
            {anioNuevoOk && anioNuevo > new Date().getFullYear() && (
              <p className="adm-note adm-note--warn" role="status">
                <i className="fas fa-circle-info" /> {t('admin.recap.newEditionFutureWarn', { year: anioNuevo })}
              </p>
            )}
            {anioNuevoOk && (editions.data ?? []).some((e) => e.anio === anioNuevo) && (
              <p className="adm-note adm-note--info">
                <i className="fas fa-layer-group" /> {t('admin.recap.newEditionExisting', { year: anioNuevo })}
              </p>
            )}
            {create.isError && (
              <p className="adm-error" role="alert">
                <i className="fas fa-triangle-exclamation" /> {getRecognitionErrorMessage(create.error, t('admin.saveError'))}
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <button type="button" className="btn btn--ghost" onClick={() => setNewYear(null)}>{t('common.cancel')}</button>
            <button type="button" className="btn btn--primary" onClick={doCreate} disabled={!anioNuevoOk || create.isPending}>
              <i className="fas fa-plus" /> {t('admin.recap.newEditionCreate')}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {aEliminar && (
        <ConfirmModal
          title={t('admin.recap.deleteTitle')}
          message={t('admin.recap.deleteConfirm', { name: aEliminar.nombre, year: aEliminar.anio })}
          confirmText={t('admin.recap.delete')}
          cancelText={t('common.cancel')}
          onConfirm={() => doDelete(aEliminar)}
          onClose={() => setAEliminar(null)}
        />
      )}
    </AdminSection>
  );
}

/** Vista previa de la edición tal como la recorrerá el usuario de la OU elegida (o Global). */
function RecapPreview({ edition, slides, onClose }: Readonly<{ edition: RecapEditionDto; slides: RecapSlideDto[]; onClose: () => void }>) {
  const { t, i18n } = useTranslation('recognition');
  // createPortal a document.body: `.plataforma-main` (recognition-shell.css) lleva `isolation:
  // isolate`, que aísla el z-index de `.modal-overlay` dentro de su propio contexto de apilamiento
  // — nunca compite contra el z-index explícito del Sidebar, que queda pintado encima. Mismo fix ya
  // aplicado en BulkUploadModal.tsx/WinnersImportWizardModal.tsx.
  return createPortal((
    <div className="modal-overlay modal-overlay--fade" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="adm-recap-preview-modal" onClick={(e) => e.stopPropagation()}>
        <button className="rec-modal-close" onClick={onClose} aria-label={t('common.close')}>
          <i className="fas fa-times" />
        </button>
        <h3 className="adm-recap-editor-title">{edition.nombre}</h3>
        <p className="adm-note">{t('admin.recap.previewNote')}</p>
        <div className="adm-recap-preview-strip">
          {slides.map((s) => (
            <div key={s.clave} className="adm-recap-slide-preview" style={estiloDeFondo(s)}>
              <span className="adm-recap-slide-icon">
                {s.clave === 'ganador' ? <i className="fas fa-award" /> : s.icono}
              </span>
              <p className="adm-recap-slide-title">{s.titulo[i18n.language] ?? s.titulo.es ?? ''}</p>
              {(s.etiquetasNumero ?? []).length > 0 && (
                <div className="adm-recap-slide-numbers">
                  {(s.etiquetasNumero ?? []).map((label) => (
                    <div key={label}><strong>—</strong><span>{label}</span></div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  ), document.body);
}

export default AdminRecap;
